import { useState } from 'react';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { PhoneIcon, EnvelopeIcon, MapPinIcon } from '@heroicons/react/24/outline';

export default function Connect() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/contact`, formData);
      alert('Message sent successfully!');
      setFormData({ name: '', phone: '', email: '', message: '' });
    } catch (err) {
      alert('Error sending message');
    }
  };

  return (
    <div className="bg-gray-50 text-gray-900 min-h-screen selection:bg-red-600 selection:text-white antialiased overflow-x-hidden">
      <Navbar />
      
      {/* Height parameter altered to min-h-screen with clean padding offsets */}
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Premium Contact Cards & Instant WhatsApp Call-to-Action */}
          <div className="lg:col-span-5 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="h-8 w-1.5 bg-red-600 rounded"></div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-950 font-sans tracking-tight">
                CONNECT WITH US
              </h2>
            </div>
            <p className="text-gray-600 font-light leading-relaxed text-sm sm:text-base">
              Need immediate answers? Choose to call us directly, drop by our offices, or initiate an instant chat with our customer desks via WhatsApp.
            </p>

            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
              <a href="tel:+2348138576446" className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors group">
                <div className="bg-red-50 p-2.5 rounded-lg mr-4 group-hover:bg-red-100 transition-colors">
                  <PhoneIcon className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider">Direct Hotline</span>
                  <span className="text-sm font-semibold text-blue-950">(+234) 8138576446</span>
                </div>
              </a>

              <a href="mailto:sajiesglobalsynergyltd@gmail.com" className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors group">
                <div className="bg-red-50 p-2.5 rounded-lg mr-4 group-hover:bg-red-100 transition-colors">
                  <EnvelopeIcon className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider">Email Inquiry</span>
                  <span className="text-sm font-semibold text-blue-950 break-all">sajiesglobalsynergyltd@gmail.com</span>
                </div>
              </a>

              <div className="flex items-center p-3 rounded-lg">
                <div className="bg-red-50 p-2.5 rounded-lg mr-4">
                  <MapPinIcon className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider">Our Location</span>
                  <span className="text-sm font-semibold text-blue-950">Lekki, Lagos State, Nigeria</span>
                </div>
              </div>
            </div>

            {/* Pulsing Modern WhatsApp Call to Action Panel */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 flex items-center justify-between shadow-sm relative overflow-hidden group">
              <div className="absolute right-0 bottom-0 top-0 w-1/3 bg-emerald-500/5 translate-x-8 translate-y-8 rounded-full pointer-events-none"></div>
              <div className="space-y-1 z-10 relative">
                <h4 className="text-base font-extrabold text-emerald-950">Chat On WhatsApp</h4>
                <p className="text-xs text-emerald-800 font-medium">Instantly connect with a live representative.</p>
              </div>
              <a 
                href="https://wa.me/+2348138576446" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="bg-emerald-500 hover:bg-emerald-600 text-white p-3 rounded-full shadow-md hover:scale-110 transition-transform duration-250 relative z-10 flex items-center justify-center shrink-0"
                aria-label="Chat on WhatsApp"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" 
                  alt="WhatsApp Chat Icon" 
                  className="h-6 w-6 invert" 
                />
              </a>
            </div>
          </div>

          {/* Right Column: Clean Form Layout */}
          <div className="lg:col-span-7 bg-white p-8 rounded-2xl border border-gray-200/60 shadow-md">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your full name"
                  required
                  className="w-full p-3 text-black rounded-lg border border-gray-200/80 focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-sm bg-gray-50"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g. +234"
                  required
                  className="w-full p-3 text-black rounded-lg border border-gray-200/80 focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-sm bg-gray-50"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="name@example.com"
                  required
                  className="w-full p-3 text-black rounded-lg border border-gray-200/80 focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-sm bg-gray-50"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us what you are looking for..."
                  rows="4"
                  required
                  className="w-full p-3 text-black rounded-lg border border-gray-200/80 focus:ring-1 focus:ring-blue-900 focus:border-blue-900 outline-none transition text-sm bg-gray-50"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold p-3.5 rounded-lg shadow-sm hover:shadow-md hover:scale-[1.01] transition duration-200 font-sans tracking-wider"
              >
                SEND MESSAGE
              </button>
            </form>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
}
